import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { formatINR } from "@/lib/format";
import { useBusinessInfo } from "@/lib/businessInfo";
import { useAuth } from "@/context/AuthContext";
import { GroupCombobox } from "@/components/GroupCombobox";

export const AddBillDialog = ({ open, onOpenChange, clients, onCreated, presetClientId }) => {
  const businessInfo = useBusinessInfo();
  const { user } = useAuth();
  const RESTRICTED_EMAILS = ["office@billing.com", "invoice@billing.com"];
  const isOfficeView = RESTRICTED_EMAILS.includes((user?.email || "").toLowerCase());
  const firms = businessInfo?.firms || [];
  const defaultFirm = businessInfo?.default_firm || firms[0]?.key || "";
  const rowIdRef = useRef(0);
  const newRow = () => ({ uid: ++rowIdRef.current, description: "", fee: "", group: "" });

  const [clientId, setClientId] = useState("");
  const [firm, setFirm] = useState(defaultFirm);
  const [createdByName, setCreatedByName] = useState("");
  const [services, setServices] = useState(() => [newRow()]);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId(presetClientId || "");
      setFirm(defaultFirm);
      setCreatedByName("");
      setServices([newRow()]);
      setNotes("");
      setDueDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetClientId, defaultFirm]);

  const total = useMemo(
    () => services.reduce((sum, s) => sum + (parseFloat(s.fee) || 0), 0),
    [services]
  );

  const updateService = (uid, key, val) => {
    setServices((arr) => arr.map((s) => (s.uid === uid ? { ...s, [key]: val } : s)));
  };

  const addRow = () => setServices((arr) => [...arr, newRow()]);
  const removeRow = (uid) =>
    setServices((arr) => (arr.length === 1 ? arr : arr.filter((s) => s.uid !== uid)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) {
      toast.error("Please select a client");
      return;
    }
    const cleaned = services
      .map((s) => ({
        description: s.description.trim(),
        fee: parseFloat(s.fee) || 0,
        group: (s.group || "").trim() || null,
      }))
      .filter((s) => s.description && s.fee > 0);
    if (cleaned.length === 0) {
      toast.error("Add at least one service with a description and fee");
      return;
    }
    if (isOfficeView && !createdByName.trim()) {
      toast.error("Please enter who is creating this invoice");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/bills", {
        client_id: clientId,
        services: cleaned,
        notes: notes || null,
        due_date: dueDate || null,
        firm: firm || null,
        created_by_name: createdByName.trim() || null,
      });
      toast.success(`Invoice ${data.invoice_no} created`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col p-0"
        data-testid="add-bill-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-display font-medium text-2xl">New Invoice</DialogTitle>
          <DialogDescription className="text-slate-500">
            Add services and fees. Total is calculated automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-tag">Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger
                  data-testid="bill-client-select"
                  className="rounded-md border-slate-300"
                >
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                      <span className="font-mono text-xs mr-2">{c.client_code}</span>
                      {c.name}{c.email ? ` — ${c.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tag">Due date (optional)</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="bill-due-date"
                className="rounded-md border-slate-300"
              />
            </div>
          </div>

          {firms.length > 1 && (
            <div className="space-y-2">
              <Label className="label-tag">Invoice in the name of</Label>
              <Select value={firm} onValueChange={setFirm}>
                <SelectTrigger
                  data-testid="bill-firm-select"
                  className="rounded-md border-slate-300"
                >
                  <SelectValue placeholder="Select firm" />
                </SelectTrigger>
                <SelectContent>
                  {firms.map((f) => (
                    <SelectItem key={f.key} value={f.key} data-testid={`firm-option-${f.key}`}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Bank details on the PDF will match the firm you pick.
              </p>
            </div>
          )}

          {isOfficeView && (
            <div className="space-y-2">
              <Label htmlFor="created-by-name" className="label-tag">
                Invoice is created by
              </Label>
              <Input
                id="created-by-name"
                placeholder="Person's name (e.g. Ravi Sharma)"
                value={createdByName}
                onChange={(e) => setCreatedByName(e.target.value)}
                data-testid="bill-created-by-name"
                className="rounded-md border-slate-300"
                required
              />
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="label-tag">Services</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                className="border-slate-300"
                data-testid="add-service-row-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add row
              </Button>
            </div>

            <div className="space-y-2">
              {services.map((s, i) => (
                <div key={s.uid} className="grid grid-cols-12 gap-2 items-start" data-testid={`service-row-${i}`}>
                  <Input
                    className="col-span-6 rounded-md border-slate-300"
                    placeholder="Service description"
                    value={s.description}
                    onChange={(e) => updateService(s.uid, "description", e.target.value)}
                    data-testid={`service-desc-${i}`}
                  />
                  <div className="col-span-3">
                    <GroupCombobox
                      value={s.group || ""}
                      onChange={(v) => updateService(s.uid, "group", v)}
                      testId={`service-group-${i}`}
                    />
                  </div>
                  <Input
                    className="col-span-2 rounded-md border-slate-300 font-mono"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Fee (₹)"
                    value={s.fee}
                    onChange={(e) => updateService(s.uid, "fee", e.target.value)}
                    data-testid={`service-fee-${i}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(s.uid)}
                    className="col-span-1 text-slate-400 hover:text-rose-600"
                    data-testid={`remove-service-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-tag">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-md border-slate-300 resize-none"
              data-testid="bill-notes"
            />
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 pt-4">
            <span className="label-tag">Total</span>
            <span className="font-mono text-2xl font-medium text-slate-900" data-testid="bill-total-preview">
              {formatINR(total)}
            </span>
          </div>
          </div>

          <DialogFooter className="border-t border-slate-100 px-6 py-3 bg-white shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-300"
              data-testid="cancel-add-bill"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="submit-add-bill"
            >
              {submitting ? "Saving…" : "Create Invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
